import React from 'react';
import './reha-mobility.css';
import { Link } from "react-router-dom";

function RehaMobility() {
  const mobilityAids = [
    {
      id: "wheelchairs",
      title: "Wheelchairs",
      description: "Manual and electric wheelchairs customized for comfort, mobility, and independence.",
      features: ["Lightweight manual", "Power wheelchairs", "Sports models", "Pediatric sizes"]
    },
    {
      id: "walking-aids",
      title: "Walking Aids",
      description: "Supportive devices to help maintain balance and reduce strain while walking.",
      features: ["Canes & crutches", "Walkers & rollators", "Forearm supports", "Quad canes"]
    },
    {
      id: "mobility-scooters",
      title: "Mobility Scooters",
      description: "Electric scooters for outdoor independence and longer-distance travel.",
      features: ["3-wheel & 4-wheel", "Portable models", "Heavy-duty options", "All-terrain"]
    },
    {
      id: "orthopedic-footwear",
      title: "Orthopedic Footwear",
      description: "Specialized shoes and inserts for foot conditions and improved gait.",
      features: ["Diabetic shoes", "Custom orthotics", "Post-op shoes", "Comfort footwear"]
    }
  ];

  const rehabilitationEquipment = [
    {
      id: "therapy-tools",
      title: "Therapy & Exercise Tools",
      items: ["Therapy bands", "Exercise balls", "Hand weights", "Balance boards"]
    },
    {
      id: "home-therapy",
      title: "Home Therapy Equipment",
      items: ["Overdoor pulleys", "Finger ladders", "Grip strengtheners", "Pedal exercisers"]
    },
    {
      id: "daily-living",
      title: "Daily Living Aids",
      items: ["Long-handled tools", "Dressing aids", "Bathroom safety", "Kitchen helpers"]
    }
  ];

  const benefits = [
    {
      id: "independence",
      title: "Restored Independence",
      description: "Regain the ability to perform daily activities and move freely in your environment."
    },
    {
      id: "safety",
      title: "Enhanced Safety",
      description: "Reduce fall risk and prevent injuries with properly fitted mobility equipment."
    },
    {
      id: "comfort",
      title: "Superior Comfort",
      description: "Ergonomic designs and custom fittings ensure all-day comfort and proper support."
    },
    {
      id: "quality",
      title: "Premium Quality",
      description: "Durable, reliable equipment from trusted manufacturers with warranty coverage."
    }
  ];

  const processSteps = [
    {
      id: "step1",
      number: "01",
      title: "Assessment",
      description: "Comprehensive evaluation of your mobility needs, living environment, and lifestyle."
    },
    {
      id: "step2",
      number: "02",
      title: "Equipment Selection",
      description: "Expert guidance in choosing the right equipment from our wide range of options."
    },
    {
      id: "step3",
      number: "03",
      title: "Custom Fitting",
      description: "Precision adjustments to ensure optimal fit, comfort, and functionality."
    },
    {
      id: "step4",
      number: "04",
      title: "Training & Support",
      description: "Hands-on training and ongoing support for you and your caregivers."
    }
  ];

  const brands = [
    { id: "brand1", name: "Invacare", logo: "/brands/invacare.png" },
    { id: "brand2", name: "Drive Medical", logo: "/brands/drive.png" },
    { id: "brand3", name: "Sunrise Medical", logo: "/brands/sunrise.png" },
    { id: "brand4", name: "Permobil", logo: "/brands/permobil.png" },
    { id: "brand5", name: "Ottobock", logo: "/brands/ottobock.png" },
    { id: "brand6", name: "Karman", logo: "/brands/karman.png" }
  ];

  const faqs = [
    {
      question: "Does insurance cover mobility equipment?",
      answer: "Most health insurance plans, including government healthcare, provide coverage for medically necessary mobility equipment. Our team will help verify your benefits."
    },
    {
      question: "How do I know which mobility aid is right for me?",
      answer: "Our specialists conduct a thorough assessment of your needs, mobility level, and home environment to recommend the most suitable equipment."
    },
    {
      question: "Can I try equipment before purchasing?",
      answer: "Yes, we offer in-clinic trials for most mobility equipment. Some items may be available for short-term home trials."
    },
    {
      question: "Do you provide maintenance and repairs?",
      answer: "Yes, we offer full service and repair support for all equipment purchased through us, including warranty repairs."
    }
  ];

  return (
    <div className="reha-mobility-page">

      {/* Hero Section */}
      <section className="mob-hero-section">
        <div className="mob-hero-overlay">
          <div className="mob-container">
            <div className="mob-hero-content">
              <span className="mob-hero-badge">Rehabilitation & Mobility Aids</span>
              <h1 className="mob-hero-title">
                Regain Your <span className="mob-hero-highlight">Freedom of Movement</span>
              </h1>
              <p className="mob-hero-subtitle">
                High-quality mobility equipment and rehabilitation aids designed to help you 
                live independently, safely, and comfortably in your home and community.
              </p>
              <div className="mob-hero-buttons">
                <Link to="/contactus" className="mob-btn mob-btn-primary">Schedule Assessment</Link>
             
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="mob-intro-section">
        <div className="mob-container">
          <div className="mob-intro-grid">
            <div className="mob-intro-content">
              <span className="mob-section-badge">About Reha Mobility</span>
              <h2 className="mob-intro-heading">Your Partner in Independent Living</h2>
              <p className="mob-intro-text">
                At Southern Rehab Care, we believe that mobility challenges shouldn't limit your 
                quality of life. Our Reha Mobility division provides a comprehensive range of 
                rehabilitation aids and mobility equipment to support your journey toward greater 
                independence.
              </p>
              <p className="mob-intro-text">
                From wheelchairs and walkers to daily living aids and therapy tools, we offer 
                personalized solutions tailored to your specific needs, abilities, and living 
                environment. Our experienced team provides expert guidance, custom fitting, and 
                ongoing support every step of the way.
              </p>
              <div className="mob-intro-features">
                <div className="mob-intro-feature">
                  <span className="mob-intro-feature-dot"></span>
                  <span>Professional Assessment</span>
                </div>
                <div className="mob-intro-feature">
                  <span className="mob-intro-feature-dot"></span>
                  <span>Custom Fitting</span>
                </div>
                <div className="mob-intro-feature">
                  <span className="mob-intro-feature-dot"></span>
                  <span>Ongoing Support</span>
                </div>
              </div>
            </div>
            <div className="mob-intro-image-wrapper">
              <img src="/reha-mobility-logo.png" alt="Mobility Assessment" className="mob-intro-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobility Aids Section */}
      <section className="mob-aids-section">
        <div className="mob-container">
          <div className="mob-section-header">
            <span className="mob-section-badge">Mobility Solutions</span>
            <h2 className="mob-section-heading">Complete Range of Mobility Aids</h2>
            <p className="mob-section-subheading">Find the right equipment for your needs</p>
          </div>
          <div className="mob-aids-grid">
            {mobilityAids.map((aid) => (
              <div key={aid.id} className="mob-aid-card">
                <h3 className="mob-aid-title">{aid.title}</h3>
                <p className="mob-aid-description">{aid.description}</p>
                <ul className="mob-aid-features">
                  {aid.features.map((feature, index) => (
                    <li key={index} className="mob-aid-feature">
                      <span className="mob-aid-feature-dot"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mob-benefits-section">
        <div className="mob-container">
          <div className="mob-benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="mob-benefit-card">
                <h3 className="mob-benefit-title">{benefit.title}</h3>
                <p className="mob-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rehabilitation Equipment Section */}
      <section className="mob-rehab-section">
        <div className="mob-container">
          <div className="mob-section-header">
            <span className="mob-section-badge">Rehabilitation Aids</span>
            <h2 className="mob-section-heading">Tools for Recovery & Therapy</h2>
            <p className="mob-section-subheading">Support your rehabilitation journey at home</p>
          </div>
          <div className="mob-rehab-grid">
            {rehabilitationEquipment.map((category) => (
              <div key={category.id} className="mob-rehab-card">
                <h3 className="mob-rehab-title">{category.title}</h3>
                <ul className="mob-rehab-items">
                  {category.items.map((item, index) => (
                    <li key={index} className="mob-rehab-item">
                      <span className="mob-rehab-item-dot"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="mob-process-section">
        <div className="mob-container">
          <div className="mob-section-header">
            <span className="mob-section-badge mob-section-badge-light">Our Process</span>
            <h2 className="mob-section-heading mob-section-heading-light">How We Help You</h2>
            <p className="mob-section-subheading mob-section-subheading-light">Simple, supportive, and thorough</p>
          </div>
          <div className="mob-process-grid">
            {processSteps.map((step) => (
              <div key={step.id} className="mob-process-card">
                <div className="mob-process-number">{step.number}</div>
                <h3 className="mob-process-title">{step.title}</h3>
                <p className="mob-process-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>





      {/* FAQ Section */}
      <section className="mob-faq-section">
        <div className="mob-container">
          <div className="mob-section-header">
            <span className="mob-section-badge">FAQ</span>
            <h2 className="mob-section-heading">Frequently Asked Questions</h2>
            <p className="mob-section-subheading">Answers to common questions about mobility equipment</p>
          </div>
          <div className="mob-faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="mob-faq-item">
                <h3 className="mob-faq-question">{faq.question}</h3>
                <p className="mob-faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="mob-cta-section">
        <div className="mob-container">
          <div className="mob-cta-content">
            <h2 className="mob-cta-heading">Ready to Improve Your Mobility?</h2>
            <p className="mob-cta-subheading">Schedule a consultation with our mobility specialists</p>
            <div className="mob-cta-buttons">
              <Link to="/contactus" className="mob-btn mob-btn-primary">Contact Us</Link>
              <Link to="/contactus" className="mob-btn mob-btn-secondary-light">Book Assessment</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default RehaMobility;